import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiError, downloadBlob, retryRequest } from "@/lib/api";
import { MapGrid } from "@/components/MapViewer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Play, Loader2, RefreshCw, Images, ArrowRight, ArrowLeft, Satellite, Sliders, Gauge, Gem } from "lucide-react";
import { cleanName } from "@/lib/format";
import MapPaletteManager from "@/components/MapPaletteManager";
import { MAP_KEYS, AreaMineralBlock, ImageryDialog, CoverageBadge, StatusChip, PlanetPreview } from "@/components/wizard/wizardShared";

export default function Step5Imagery({ project, reload, onNext, onBack }) {
  const [areas, setAreas] = useState(project.areas || []);
  const [commodities, setCommodities] = useState([]);
  const [running, setRunning] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [imgArea, setImgArea] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoImg, setAutoImg] = useState(false);
  const [probing, setProbing] = useState(false);
  const [showAutoOpts, setShowAutoOpts] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [autoFrom, setAutoFrom] = useState("2024-01-01");
  const [autoTo, setAutoTo] = useState("2024-12-31");
  const [autoCloud, setAutoCloud] = useState(60);
  const pollRef = useRef(null);
  const probedRef = useRef(false);

  useEffect(() => { setAreas(project.areas || []); }, [project.areas]);

  useEffect(() => {
    api.get("/catalog").then(({ data }) => {
      const cp = data?.commodity_profiles;
      setCommodities(cp ? Object.entries(cp).map(([k, v]) => ({ value: k, label: v.label || k }))
                        : (data?.commodities || []).map((c) => ({ value: c, label: c })));
    }).catch(() => {});
  }, []);

  const probeCoverage = useCallback(async (silent = false) => {
    setProbing(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/areas/coverage-probe`);
      if (!silent) toast.success(`Imagery coverage checked for ${data.count} area(s)`);
      await reload();
    } catch (e) { if (!silent) toast.error(apiError(e)); }
    finally { setProbing(false); }
  }, [project.id, reload]);

  useEffect(() => {
    if (probedRef.current) return;
    const list = project.areas || [];
    const needs = list.some((a) => a.status !== "error" && a.imagery_coverage_pct == null
      && !["queued", "processing"].includes(a.status));
    if (needs && list.length > 0) { probedRef.current = true; probeCoverage(true); }
  }, [project.areas, probeCoverage]);

  const anyBusy = areas.some((a) => ["queued", "processing"].includes(a.status) || ["queued", "processing"].includes(a.minerals_status));

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/areas/status`);
      setAreas((prev) => prev.map((a) => {
        const s = data.areas.find((x) => x.area_id === a.area_id);
        return s ? { ...a, ...s } : a;
      }));
      if (!data.areas.some((a) => ["queued", "processing"].includes(a.status) || ["queued", "processing"].includes(a.minerals_status))) {
        clearInterval(pollRef.current); setRunning(false); await reload();
        toast.success("All areas processed");
      }
    } catch (_) { /* silent */ }
  }, [project.id, reload]);

  useEffect(() => {
    if (anyBusy && !pollRef.current) { pollRef.current = setInterval(poll, 3000); setRunning(true); }
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [anyBusy, poll]);

  const refreshMaps = async () => {
    setRefreshing(true);
    try {
      await api.post(`/projects/${project.id}/areas/refresh-targets`);
      toast.info("Refreshing target maps from stored analysis - reopen 'View Maps' in ~30s");
      setTimeout(async () => { await reload(); setRefreshing(false); }, 25000);
    } catch (e) { setRefreshing(false); toast.error(apiError(e)); }
  };

  const autoImagery = async () => {
    setAutoImg(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/areas/auto-imagery?date_from=${autoFrom}&date_to=${autoTo}&max_cloud=${autoCloud}`);
      toast.success(`Best gap-free imagery mosaicked for ${data.count} area(s) - boundary coverage shown on each card`);
      setShowAutoOpts(false);
      await reload();
    } catch (e) { toast.error(apiError(e)); } finally { setAutoImg(false); }
  };

  const generateAll = async () => {
    setRunning(true);
    try {
      await retryRequest(() => api.post(`/projects/${project.id}/areas/generate`), { retries: 1, delay: 2000 });
      toast.info("Processing every area independently… this can take a few minutes");
      await reload(); // refresh parent so StepBar locks steps 6/7 while areas reprocess
      clearInterval(pollRef.current); pollRef.current = setInterval(poll, 3000);
    } catch (e) { setRunning(false); toast.error(apiError(e)); }
  };

  const bulkApplyMinerals = async () => {
    const mins = project.commodities || (project.commodity ? [project.commodity] : []);
    if (!mins.length) return toast.info("Add Target Minerals in Project Setup (Step 1) first");
    try {
      const { data } = await api.post(`/projects/${project.id}/areas/apply-minerals`, { minerals: mins });
      toast.success(`Applied ${data.minerals.length} mineral(s) to ${data.applied} area(s)`);
      await reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const retry = async (aid) => {
    try {
      await retryRequest(() => api.post(`/projects/${project.id}/areas/generate?area_id=${aid}`), { retries: 1, delay: 2000 });
      toast.info(`Retrying ${aid}…`);
      clearInterval(pollRef.current); pollRef.current = setInterval(poll, 3000); setRunning(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewMaps = async (aid) => {
    try { const { data } = await api.get(`/projects/${project.id}/areas/${aid}/maps`); setViewer({ aid, maps: data.maps || {} }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const valid = areas.filter((a) => a.status !== "error");
  const anyComplete = areas.some((a) => a.status === "complete");
  const bulkMins = project.commodities || (project.commodity ? [project.commodity] : []);

  return (
    <div className="space-y-6" data-testid="step5-imagery">
      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent data-testid="bulk-apply-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Gem size={18} className="text-primary" /> Apply Target Minerals to every licence?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the commodities on all {valid.length} licence(s) with the project Target Minerals
              {" "}(<span className="font-semibold text-foreground">{bulkMins.join(", ") || "none selected"}</span>).
              Any commodities you selected per licence will be overwritten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-apply-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-apply-confirm-btn" onClick={() => { setConfirmBulk(false); bulkApplyMinerals(); }}>Replace on all licences</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-card border border-border rounded-xl shadow-sm p-6">
        <div className="mb-4 min-w-0">
          <h2 className="text-lg font-bold text-foreground tracking-tight">Imagery &amp; Prospectivity</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">Acquire the best cloud-free satellite imagery for each licence, then process every area independently - spectral, structural and target analysis fused into a prospectivity model.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:justify-end">
          <div className="relative flex w-full sm:w-auto shrink-0">
            <Button data-testid="auto-mosaic-btn" onClick={autoImagery} disabled={autoImg || running || valid.length === 0} variant="outline"
              className="border-border h-11 pl-4 pr-2.5 text-sm font-semibold rounded-r-none whitespace-nowrap flex-1 sm:flex-none">
              {autoImg ? <><Loader2 size={16} className="mr-2 animate-spin" />Mosaicking…</> : <><Satellite size={16} className="mr-2" />Auto-Mosaic All Areas</>}
            </Button>
            <Button data-testid="auto-mosaic-opts-btn" onClick={() => setShowAutoOpts((v) => !v)} disabled={running} variant="outline"
              className="border-border border-l-0 h-11 px-2.5 rounded-l-none" title="Imagery date range & cloud filter"><Sliders size={16} /></Button>
            {showAutoOpts && (
              <div className="absolute right-0 z-30 mt-2 w-72 bg-card border border-border rounded-lg shadow-lg p-4 space-y-3 text-left" data-testid="auto-mosaic-opts">
                <div className="text-xs font-bold text-foreground">Auto-Mosaic imagery filter</div>
                <div><Label className="text-xs">Max cloud cover: {autoCloud}%</Label><Input type="range" min="0" max="100" value={autoCloud} onChange={(e) => setAutoCloud(e.target.value)} className="mt-1" data-testid="auto-cloud" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">From</Label><Input type="date" value={autoFrom} onChange={(e) => setAutoFrom(e.target.value)} className="h-9 mt-1" data-testid="auto-from" /></div>
                  <div><Label className="text-xs">To</Label><Input type="date" value={autoTo} onChange={(e) => setAutoTo(e.target.value)} className="h-9 mt-1" data-testid="auto-to" /></div>
                </div>
              </div>
            )}
          </div>
          {anyComplete && (
            <Button data-testid="refresh-maps-btn" onClick={refreshMaps} disabled={refreshing || running} variant="outline"
              className="border-border h-11 px-4 text-sm font-semibold whitespace-nowrap w-full sm:w-auto">
              {refreshing ? <><Loader2 size={16} className="mr-2 animate-spin" />Refreshing…</> : <><RefreshCw size={16} className="mr-2" />Refresh Maps</>}
            </Button>
          )}
          <Button data-testid="check-coverage-btn" onClick={() => probeCoverage(false)} disabled={probing || running || valid.length === 0} variant="outline"
            className="border-border h-11 px-4 text-sm font-semibold whitespace-nowrap w-full sm:w-auto">
            {probing ? <><Loader2 size={16} className="mr-2 animate-spin" />Checking…</> : <><Gauge size={16} className="mr-2" />Check Coverage</>}
          </Button>
          <Button data-testid="bulk-apply-minerals-btn" onClick={() => setConfirmBulk(true)} disabled={running || valid.length === 0} variant="outline"
            className="border-border h-11 px-5 text-sm font-semibold w-full sm:w-auto"
            title={`Apply the project's target minerals (${bulkMins.join(", ") || "none selected"}) to every area at once`}>
            <Gem size={16} className="mr-2" /> Apply Minerals to All
          </Button>
          <Button data-testid="generate-all-btn" onClick={generateAll} disabled={running || valid.length === 0}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-5 text-sm font-semibold whitespace-nowrap w-full sm:w-auto">
            {running ? <><Loader2 size={16} className="mr-2 animate-spin" />Processing…</> : <><Play size={16} className="mr-2" />Generate All Areas</>}
          </Button>
        </div>
      </div>

      {anyComplete && (
        <MapPaletteManager projectId={project.id} onApplied={() => { setRefreshing(true); setTimeout(async () => { await reload(); setRefreshing(false); }, 25000); }} />
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="area-progress-cards">
        {areas.map((a) => (
          <div key={a.area_id} className="bg-card border border-border rounded-xl shadow-sm p-4 min-w-0" data-testid={`area-card-${a.area_id}`}>
            <div className="flex items-center flex-wrap gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground grid place-items-center font-extrabold text-sm shrink-0">{a.area_id}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground truncate">{cleanName(a.name)}</div>
                <div className="text-[11px] text-muted-foreground">{(a.boundary_metrics || {}).area_km2 ?? "-"} km²</div>
              </div>
              <CoverageBadge a={a} probing={probing} />
              {a.effective_res_m != null && (
                <span data-testid={`area-res-${a.area_id}`} title={`True ground sampling of the analysis grid (Sensor: Sentinel-2, ${a.sensor_res_m || 10} m native)`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 shrink-0">
                  <Gauge size={10} /> ~{a.effective_res_m} m/px
                </span>
              )}
              {a.imagery_widened && (
                <span data-testid={`area-widened-${a.area_id}`} title="The date range was automatically widened to gather extra passes and fill imagery gaps to reach this coverage"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 shrink-0">
                  <RefreshCw size={10} /> Auto-widened
                </span>
              )}
              <StatusChip a={a} />
            </div>

            {["queued", "processing"].includes(a.status) && (
              <div className="mt-3" data-testid={`area-progress-${a.area_id}`}>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${a.progress || 0}%` }} />
                </div>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span className="text-[11px] text-muted-foreground">{a.stage || "Queued"} · {a.progress || 0}%</span>
                  <span className="text-[11px] text-muted-foreground tabular-nums" data-testid={`area-eta-${a.area_id}`}>
                    {(() => {
                      if (!a.started_at || a.progress >= 100) return "";
                      const el = (Date.now() - new Date(a.started_at).getTime()) / 1000;
                      if (el < 3 || (a.progress || 0) < 5) return "estimating…";
                      const rem = Math.max(0, Math.round(el / a.progress * (100 - a.progress)));
                      return rem >= 60 ? `~${Math.ceil(rem / 60)} min left` : `~${rem}s left`;
                    })()}
                  </span>
                </div>
              </div>
            )}

            {a.status === "complete" && (
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-muted-foreground" data-testid={`area-summary-${a.area_id}`}>
                  5 Maps · {a.target_count || 0} Targets · {a.high_priority_count || 0} High Priority
                  {a.data_confidence != null && (
                    <> · <span className="font-semibold text-emerald-700" data-testid={`area-confidence-${a.area_id}`}>{Math.round(a.data_confidence)}/100 confidence</span></>
                  )}
                </div>
                <Button size="sm" variant="outline" className="border-border" onClick={() => viewMaps(a.area_id)} data-testid={`area-view-maps-${a.area_id}`}>
                  <Images size={14} className="mr-1.5" /> View Maps
                </Button>
              </div>
            )}

            {a.status === "failed" && (
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-red-600 truncate max-w-[70%]">{a.error || "Processing failed"}</div>
                <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={() => retry(a.area_id)} data-testid={`area-retry-${a.area_id}`}>
                  <RefreshCw size={14} className="mr-1.5" /> Retry
                </Button>
              </div>
            )}

            {a.status === "error" && <div className="mt-3 text-xs text-red-600">Invalid boundary - remove or re-upload in the previous step.</div>}

            {a.status !== "error" && (
              <AreaMineralBlock project={project} area={a} commodities={commodities} />
            )}

            {a.status !== "error" && (
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-[11px] text-muted-foreground truncate min-w-0" data-testid={`area-imagery-status-${a.area_id}`}>
                    {a.imagery_coverage_pct != null
                      ? <>Imagery: {a.mosaic_count ?? 1} scene{(a.mosaic_count ?? 1) > 1 ? "s" : ""} mosaicked · {a.imagery_coverage_pct}% coverage{a.effective_res_m ? ` · ~${a.effective_res_m} m/px` : ""}{a.imagery_widened ? " · date range auto-widened" : ""}</>
                      : a.selected_scene
                        ? <>Imagery: {(a.selected_scene.datetime || "").slice(0, 10)} · ☁ {a.selected_scene.cloud_cover ?? "-"}% · cov {a.selected_scene.coverage_pct ?? "-"}%</>
                        : <>Imagery: auto-select best available scene</>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PlanetPreview a={a} />
                  <Button size="sm" variant="outline" className="border-border h-7 text-xs shrink-0" onClick={() => setImgArea(a)} data-testid={`area-imagery-${a.area_id}`}>
                    <Satellite size={13} className="mr-1.5" /> {a.selected_scene ? "Change Imagery" : "Select Imagery"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" className="border-border h-11 px-5 text-sm font-semibold" onClick={onBack} data-testid="imagery-back-btn">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <Button data-testid="to-target-review-btn" onClick={onNext} disabled={!anyComplete}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold disabled:opacity-40">
          Review Targets <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-[92vw] w-[92vw] max-h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="area-maps-dialog">
          <DialogTitle className="px-6 py-4 border-b border-border dark:border-slate-700 text-foreground dark:text-slate-100 font-bold shrink-0">
            {viewer?.aid} - {viewer?.name || "Maps"}
          </DialogTitle>
          <div className="bg-muted dark:bg-slate-900 p-4 overflow-auto">
            {viewer && (
              <MapGrid items={MAP_KEYS} maps={viewer.maps}
                onHd={(key) => downloadBlob(`/projects/${project.id}/areas/${viewer.aid}/map/${key}`, `${viewer.aid}_${key}.png`)} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ImageryDialog project={project} area={imgArea} onClose={() => setImgArea(null)} reload={reload} />
    </div>
  );
}
